import Link from 'next/link'
import { ButtonHTMLAttributes, ReactNode } from 'react'

// ============================================================================
// BUTTON — shared primitive used across NovaVerse.
// Renders as an anchor (Next Link) when `href` is provided, else a <button>.
// ============================================================================

type Variant = 'primary' | 'secondary' | 'ghost'
type Size = 'sm' | 'md' | 'lg'

const base =
  'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nova-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60'

const variants: Record<Variant, string> = {
  primary:
    'bg-gradient-to-r from-nova-600 to-aqua-500 text-white shadow-lg shadow-nova-600/25 hover:shadow-xl hover:shadow-nova-600/40 hover:-translate-y-0.5 active:translate-y-0',
  secondary:
    'border border-border bg-surface/70 text-foreground backdrop-blur hover:border-nova-400/60 hover:text-nova-500',
  ghost: 'text-foreground/80 hover:bg-foreground/5 hover:text-foreground',
}

const sizes: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-6 text-sm',
  lg: 'h-14 px-8 text-base',
}

interface BaseProps {
  variant?: Variant
  size?: Size
  className?: string
  children: ReactNode
}

interface LinkButtonProps extends BaseProps {
  href: string
}

type NativeButtonProps = BaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined }

export function Button(props: LinkButtonProps | NativeButtonProps): React.JSX.Element {
  const {
    variant = 'primary',
    size = 'md',
    className = '',
    children,
  } = props
  const classes = `${base} ${variants[variant]} ${sizes[size]} ${className}`.trim()

  if ('href' in props && props.href) {
    return (
      <Link href={props.href} className={classes}>
        {children}
      </Link>
    )
  }

  const { variant: _v, size: _s, className: _c, children: _ch, ...rest } =
    props as NativeButtonProps
  return (
    <button className={classes} {...rest}>
      {children}
    </button>
  )
}
